import { memo, useEffect, useRef, useState } from "react";
import { classNames } from "helpers";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import Logo from "ui/modules/public/components/Logo/Logo";

type ModalIntroContentProps = {
  animation: object;
  title: string;
  description: string;
  show: boolean;
  animationSize?: string;
};

const ModalIntro: React.FC<ModalIntroContentProps> = (props) => {
  const {
    animation,
    title,
    description,
    show,
    animationSize = "w-[500px]",
  } = props;

  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    if (!show) {
      setPhase(0);
      return;
    }

    const timer = setTimeout(() => setPhase(1), 150);
    return () => clearTimeout(timer);
  }, [show]);

  useEffect(() => {
    if (phase > 0 && lottieRef.current) {
      lottieRef.current.setSpeed(0.5);
      lottieRef.current.goToAndPlay(0);
    }
  }, [phase]);

  const handleLoopComplete = () => {
    if (lottieRef.current) {
      lottieRef.current.stop();
      const totalFrames = lottieRef.current.getDuration(true) ?? 0;
      if (totalFrames > 0) {
        lottieRef.current.goToAndStop(totalFrames - 1, true);
      }
    }
  };

  const isTextVisible = phase > 0;

  return (
    <div className="bg-blue-700 w-full h-full">
      <div className="relative w-full h-full flex flex-col items-center justify-center overflow-hidden">
        <Logo
          variant="light"
          animated
          className={classNames(
            "absolute top-8 left-8 transition-opacity duration-700 delay-300",
            isTextVisible ? "opacity-100" : "opacity-0"
          )}
        />
        <Lottie
          lottieRef={lottieRef}
          animationData={animation}
          autoplay={false}
          loop={true}
          onLoopComplete={handleLoopComplete}
          className={classNames(animationSize, "h-auto")}
        />
        <div
          className={classNames(
            "text-center transition-all duration-700 ease-out delay-500 max-w-md",
            isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-16"
          )}
        >
          <h1 className="text-4xl leading-[46px] font-bold mb-3 text-white">
            {title}
          </h1>
          <p className="text-lg opacity-80 text-blue-50">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default memo(ModalIntro);
